import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { useContent } from "../../i18n/content";
import { SectionHeader } from "../SectionHeader";
import { Reveal, EASE } from "../motion/Reveal";

export const Roadmap = () => {
    const [active, setActive] = useState(0);
    const { ui, site } = useContent();
    const stage = site.roadmap[active];

    return (
        <section id="roadmap" data-testid="roadmap-section" className="relative py-24 lg:py-32 bg-[#24251f] overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                <SectionHeader
                    chapter={ui.roadmap.chapter}
                    overline={ui.roadmap.overline}
                    title={ui.roadmap.title}
                    description={ui.roadmap.description}
                />

                {/* Desktop horizontal timeline */}
                <div className="hidden md:block">
                    <div className="relative">
                        <div className="absolute top-[26px] left-0 right-0 h-px bg-[#4a4c42]" aria-hidden="true" />
                        <motion.div
                            className="absolute top-[26px] left-0 h-px bg-[#f6c422] yellow-glow"
                            initial={false}
                            animate={{ width: `${(active / (site.roadmap.length - 1)) * 100}%` }}
                            transition={{ duration: 0.7, ease: EASE }}
                            aria-hidden="true"
                        />
                        <div className="relative grid grid-cols-5">
                            {site.roadmap.map((r, i) => (
                                <button
                                    key={r.year}
                                    data-testid={`roadmap-year-${r.year}`}
                                    onClick={() => setActive(i)}
                                    aria-pressed={active === i}
                                    className="group flex flex-col items-center gap-4 pt-0 pb-2 focus-visible:outline-none"
                                >
                                    <span
                                        className={`w-[13px] h-[13px] rotate-45 border transition-all duration-500 mt-[20px] ${
                                            i <= active
                                                ? "bg-[#f6c422] border-[#f6c422] yellow-glow"
                                                : "bg-[#24251f] border-[#4a4c42] group-hover:border-[#f6c422]"
                                        }`}
                                    />
                                    <span
                                        className={`font-display text-2xl lg:text-3xl font-extrabold transition-colors duration-300 ${
                                            active === i ? "text-[#f6c422]" : i < active ? "text-white/70" : "text-white/30 group-hover:text-white/60"
                                        }`}
                                    >
                                        {r.year}
                                    </span>
                                    <span
                                        className={`font-display text-[10px] font-semibold tracking-[0.24em] uppercase transition-colors duration-300 ${
                                            active === i ? "text-white" : "text-[#a2a59d]/50"
                                        }`}
                                    >
                                        {r.title}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={stage.year}
                            data-testid="roadmap-detail-panel"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.5, ease: EASE }}
                            className="mt-14 border border-[#4a4c42] bg-[#292a24] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10"
                        >
                            <div className="lg:col-span-4">
                                <span className="font-display text-6xl lg:text-7xl font-extrabold text-stroke-yellow">
                                    {stage.year}
                                </span>
                                <h3 className="font-display text-xl font-bold uppercase tracking-[0.14em] text-white mt-4">
                                    {stage.title}
                                </h3>
                                <p className="text-[#a2a59d] text-sm mt-3 leading-relaxed">{stage.note}</p>
                                {stage.markets && (
                                    <div className="mt-6">
                                        <p className="overline-text !text-[10px] mb-3">{ui.roadmap.targetMarkets}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {stage.markets.map((m) => (
                                                <span
                                                    key={m}
                                                    className="text-xs font-display font-semibold tracking-wider uppercase text-[#f6c422] border border-[#f6c422]/40 px-3 py-1.5"
                                                >
                                                    {m}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 content-start">
                                {stage.bullets.map((b) => (
                                    <li key={b} className="flex items-start gap-3 text-sm text-white/85 leading-relaxed">
                                        <Check className="w-4 h-4 text-[#f6c422] shrink-0 mt-0.5" strokeWidth={2.5} />
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Mobile vertical timeline */}
                <div className="md:hidden relative pl-8">
                    <div className="absolute left-[6px] top-2 bottom-2 w-px bg-[#4a4c42]" aria-hidden="true" />
                    {site.roadmap.map((r, i) => (
                        <Reveal key={r.year} delay={0.06 * i}>
                            <div data-testid={`roadmap-year-mobile-${r.year}`} className="relative pb-12 last:pb-0">
                                <span className="absolute -left-8 top-2 w-[13px] h-[13px] rotate-45 bg-[#f6c422] border border-[#f6c422]" />
                                <span className="font-display text-3xl font-extrabold text-[#f6c422]">{r.year}</span>
                                <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white mt-1">
                                    {r.title}
                                </h3>
                                {r.markets && (
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {r.markets.map((m) => (
                                            <span key={m} className="text-[10px] font-display font-semibold tracking-wider uppercase text-[#f6c422] border border-[#f6c422]/40 px-2.5 py-1">
                                                {m}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <ul className="mt-4 space-y-2.5">
                                    {r.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2.5 text-sm text-white/80 leading-relaxed">
                                            <Check className="w-3.5 h-3.5 text-[#f6c422] shrink-0 mt-1" strokeWidth={2.5} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
